import { YTimeString, YDateString } from './types';
import { $format } from './format';

declare global {
    interface Date {
        y_getWeek(): number;
        y_getHours12(): number;
        y_getUTCWeek(): number;
        y_getUTCHours12(): number;
        y_getUTCQuarter(): string;
        y_getAgoString(): string;
        y_getAgoDateString(): string;
    }
}

Date.prototype.y_getWeek = function(this: Date): number {
    const y = this.getFullYear();
    const first_weekday = (new Date(y, 0, 1)).getDay();
    const week_1_start = new Date(y, 0, 10 - (first_weekday + 2) % 7).getTime() - 7 * 86400000;
    const this_week_start = this.getTime() - this.getDay() * 86400000;
    return 1 + Math.round((this_week_start - week_1_start) / (7 * 86400000));
}

Date.prototype.y_getHours12 = function(this: Date): number {
    const h = this.getHours();
    if(h == 0) return 12;
    if(h > 12) return h - 12;
    return h;
}

Date.prototype.y_getUTCWeek = function(this: Date): number {
    const y = this.getUTCFullYear();
    const first_weekday = new Date(Date.UTC(y, 0, 1)).getUTCDay();
    const week_1_start = new Date(Date.UTC(y, 0, 10 - (first_weekday + 2) % 7)).getTime() - 7 * 86400000;
    const this_week_start = this.getTime() - this.getUTCDay() * 86400000;
    return 1 + Math.round((this_week_start - week_1_start) / (7 * 86400000));
}

Date.prototype.y_getUTCHours12 = function(this: Date): number {
    const h = this.getUTCHours();
    if(h == 0) return 12;
    if(h > 12) return h - 12;
    return h;
}

Date.prototype.y_getUTCQuarter = function(this: Date): string {
    return 'Q' + (Math.floor(this.getUTCMonth() / 3) + 1);
}

Date.prototype.y_getAgoString = function(this: Date): string {
    const diff = Date.now() - this.getTime()

    if(diff < 500) {
        return 'Now';
    } else if(diff < 1500) {
        return '1 second ago';
    } else if(diff < 60000) {
        return Math.round(diff / 1000) + ' seconds ago'
    } else if(diff < 90000) {
        return '1 minute ago';
    } else if(diff < 3600000) {
        return Math.round(diff / 60000) + ' minutes ago'
    } else if(diff < 5400000) {
        return '1 hour ago';
    } else if(diff < 86400000) {
        return Math.round(diff / 3600000) + ' hours ago'
    } else if(diff < 129600000) {
        return '1 day ago';
    } else if(diff < 30 * 86400000) {
        return Math.round(diff / 86400000) + ' days ago'
    } else if(diff < 45 * 86400000) {
        return '1 month ago';
    } else {
        return Math.round(diff / (30 * 86400000)) + ' months ago'
    }
}

Date.prototype.y_getAgoDateString = function(this: Date): string {
    const diff = Date.now() - this.getTime()

    if(diff < 86400000) {
        return 'Today';
    } else if(diff < 129600000) {
        return '1 day ago';
    } else if(diff < 30 * 86400000) {
        return Math.round(diff / 86400000) + ' days ago'
    } else if(diff < 45 * 86400000) {
        return '1 month ago';
    } else {
        return Math.round(diff / (30 * 86400000)) + ' months ago'
    }
}

const formatters: { [tz: string]: Intl.DateTimeFormat } = {};

function convertToTimezone(date: YTime, tz: string) {
    let formatter = formatters[tz];
    if(!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: "numeric", month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' });
        formatters[tz] = formatter;
    }

    const parts: { [key: string]: string } = {};

    for(const part of formatter.formatToParts(date.n)) {
        parts[part.type] = part.value;
    }

    return Date.UTC(parseInt(parts['year']), parseInt(parts['month']) - 1, parseInt(parts['day']), parseInt(parts['hour']), parseInt(parts['minute']), parseInt(parts['second']), date.milliseconds);
}

export interface YTimeScale {
    duration: number;
    floor_n?: (a: number) => number;
    floor_step_n?: (a: number, s: number) => number;
    add_n?: (a: number, d: number) => number;
    sub_n?: (a: number, b: number) => number;
    floor?: (a: Date) => void;
    floor_step?: (a: Date, s: number) => number;
    ceil?: (a: Date, s: number) => void;
    add?: (a: Date, d: number) => void;
    sub?: (a: Date, b: Date) => number;
}

export interface YDateScale {
    duration: number;
    floor_date: (a: Date) => [number, number, number];
    add_date: (a: Date, d: number) => [number, number, number];
    sub_date: (a: Date, b: Date) => number;
}

export class YTime {
    readonly n: number;
    private d?: Date;

    constructor(time: number) {
        this.n = time;
    }

    roundToQuarter(down?: boolean) {
        const d = this._ensureD()
        const minute = d.getMinutes();
        return new YTime(new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(),
            down ? Math.floor(minute / 15) * 15 : Math.round(minute / 15) * 15).getTime())
    }

    floor(scale: YTimeScale) {
        if(scale.floor_n) {
            return new YTime(scale.floor_n(this.n));
        } else {
            let d = new Date(this.n);
            scale.floor!(d);
            return new YTime(d.getTime());
        }
    }

    add(diff: number, scale: YTimeScale) {
        if(scale.add_n) {
            return new YTime(scale.add_n(this.n, diff));
        } else {
            let d = new Date(this.n);
            scale.add!(d, diff);
            return new YTime(d.getTime());
        }
    }

    sub(other: YTime, scale: YTimeScale) {
        if(scale.sub_n) {
            return scale.sub_n(this.n, other.n);
        } else {
            let td = new Date(this.n);
            let od = new Date(other.n);
            return scale.sub!(td, od);
        }
    }

    ceil(scale: YTimeScale, step: number = 1) {
        if(scale.floor_step_n) {
            return new YTime(scale.add_n!(scale.floor_step_n(this.n - 1, step), step));
        } else {
            let d = new Date(this.n - 1);
            scale.floor!(d);
            scale.add!(d, 1);
            scale.floor!(d);
            return new YTime(d.getTime());
        }
    }

    format(f: string, zero: string = '-'): string {
        if(!this.n) return zero;

        return $format.timeLocal(this._ensureD(), f);
    }

    formatIn(f: string, tz: string) {
        if(tz === 'local')
            return $format.timeLocal(this._ensureD(), f);
        else if(tz === 'UTC')
            return $format.timeUTC(this._ensureD(), f);

        return $format.timeUTC(new Date(convertToTimezone(this, tz)), f);
    }

    inRange(from: YTime | undefined, to: YTime | undefined) {
        return (!from || this.n >= from.n) && (!to || this.n <= to.n);
    }

    before(rhs: YTime) {
        return this.n < rhs.n;
    }

    after(rhs: YTime) {
        return this.n > rhs.n;
    }

    equal(rhs: YTime) {
        return this.n == rhs.n;
    }

    get is_future() {
        return this.n > Date.now();
    }

    get is_past() {
        return this.n < Date.now();
    }

    get is_zero() {
        return !this.n;
    }

    get day_of_month() {
        return this._ensureD().getDate();
    }

    get day_of_week() {
        return this._ensureD().getDay();
    }

    get month() {
        return this._ensureD().getMonth();
    }

    get year() {
        return this._ensureD().getFullYear();
    }

    get hours() {
        return this._ensureD().getHours();
    }

    get minutes() {
        return this._ensureD().getMinutes();
    }

    get seconds() {
        return this._ensureD().getSeconds();
    }

    get milliseconds() {
        return this._ensureD().getMilliseconds();
    }

    get js() {
        return this._ensureD();
    }

    get date() {
        const d = this._ensureD();
        if(d.getTime())
            return new YDate(d.getFullYear(), d.getMonth(), d.getDate());
        else
            return new YDate;
    }

    toJSON(): YTimeString {
        if(!this.n)
            return '0001-01-01T00:00:00Z' as YTimeString;
        return this._ensureD().toJSON() as YTimeString;
    }

    private _ensureD() {
        if(!this.d) this.d = new Date(this.n);
        return this.d;
    }
}

export class YDate {
    private d: Date;

    constructor()
    constructor(year: number, month: number, day: number)
    constructor(year?: number, month?: number, day?: number) {
        this.d = new Date(year ? Date.UTC(year, month!, day!) : 0);
    }

    add(diff: number, scale: YDateScale) {
        const r = scale.add_date(this.d, diff);
        return new YDate(r[0], r[1], r[2]);
    }

    sub(other: YDate, scale: YDateScale) {
        return scale.sub_date(this.d, other.d);
    }

    floor(scale: YDateScale) {
        const r = scale.floor_date(this.d);
        return new YDate(r[0], r[1], r[2]);
    }

    format(f: string, zero: string = '-'): string {
        if(!this.d.getTime()) return zero;

        return $format.timeUTC(this.d, f);
    }

    before(rhs: YDate) {
        return this.d.getTime() < rhs.d.getTime();
    }

    equal(rhs: YDate) {
        return this.d.getTime() == rhs.d.getTime();
    }

    after(rhs: YDate) {
        return this.d.getTime() > rhs.d.getTime();
    }

    get week_start() {
        return this.add(-(this.day_of_week + 6) % 7, $time.day);
    }

    get month_start() {
        return this.add(-this.day_of_month + 1, $time.day);
    }

    get year_start() {
        return new YDate(this.year, 0, 1);
    }

    get day_string() {
        return this.format('yyyyMMdd');
    }

    get day_of_month() {
        return this.d.getUTCDate();
    }

    get day_of_week() {
        return this.d.getUTCDay();
    }

    get day_of_year() {
        return this.sub(this.year_start, $time.day);
    }

    get week_number() {
        return this.d.y_getUTCWeek();
    }

    get month() {
        return this.d.getUTCMonth();
    }

    get year() {
        return this.d.getUTCFullYear();
    }

    get start() {
        const d = this.d;
        return new YTime(d.getTime() ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0).getTime() : 0);
    }

    get end() {
        const d = this.d;
        return new YTime(d.getTime() ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 24, 0, 0, 0).getTime() : 0);
    }

    get is_today() {
        const d = new Date;
        return this.year == d.getFullYear() && this.month == d.getMonth() && this.day_of_month == d.getDate();
    }

    get n() {
        return this.d.getTime();
    }

    time(hour: number, minute: number, second?: number, ms?: number) {
        const d = this.d;
        return new YTime(d.getTime() ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), hour, minute, second || 0, ms || 0).getTime() : 0);
    }

    toJSON() {
        if(!this.d.getTime())
            return '0001-01-01T00:00:00Z';
        return this.d.toJSON();
    }
}

export function $time(time: YTimeString | number | undefined | Date | YTime) {
    if(!time) return new YTime(0);
    if(time instanceof YTime) return time;

    if(typeof time === 'string') {
        if(time === '0001-01-01T00:00:00Z')
            return new YTime(0);
        else
            return new YTime(Date.parse(time));
    }

    if(typeof time === 'number')
        return new YTime(time);

    return new YTime(time.getTime());
}

export namespace $time {
    export function getLocalTimeZone() {
        return new Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    export const millisecond: YTimeScale = {
        duration: 1,
        floor_n: (a) => a,
        floor_step_n: (a, s) => a - a % s,
        add_n: (a, d) => a + d,
        sub_n: (a, b) => a - b
    }

    export const second: YTimeScale = {
        duration: 1000,
        floor_n: (a) => Math.floor(a / 1000) * 1000,
        floor_step_n: (a, s) => Math.floor(a / (s * 1000)) * s * 1000,
        add_n: (a, d) => a + d * 1000,
        sub_n: (a, b) => (a - b) / 1000
    }

    export const minute: YTimeScale = {
        duration: 60000,
        floor_n: (a) => Math.floor(a / 60000) * 60000,
        floor_step_n: (a, s) => Math.floor(a / (s * 60000)) * s * 60000,
        add_n: (a, d) => a + d * 60000,
        sub_n: (a, b) => (a - b) / 60000
    }

    export const hour: YTimeScale = {
        duration: 3600000,
        floor_n: (a) => Math.floor(a / 3600000) * 3600000,
        floor_step_n: (a, s) => Math.floor(a / 3600000 - new Date(a).getHours() % s) * 3600000,
        add_n: (a, d) => a + d * 3600000,
        sub_n: (a, b) => (a - b) / 3600000
    }

    export const day: YTimeScale & YDateScale = {
        duration: 86400000,
        floor: (a) => { a.setHours(0, 0, 0, 0) },
        add: (a, d) => { a.setDate(a.getDate() + d) },
        sub: (a, b) => ((+a - +b - (a.getTimezoneOffset() - b.getTimezoneOffset()) * 60000) / 86400000),
        floor_date: d => {
            return [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()];
        },
        add_date: (d, n) => {
            return [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n];
        },
        sub_date: (a, b) => (+a - +b) / 86400000
    }

    export const week: YTimeScale & YDateScale = {
        duration: 604800000,
        floor: (a) => {
            a.setDate(a.getDate() - (a.getDay() + 6) % 7);
            a.setHours(0, 0, 0, 0);
        },
        add: (a, d) => { a.setDate(a.getDate() + d * 7) },
        sub: (a, b) => ((+a - +b - (a.getTimezoneOffset() - b.getTimezoneOffset()) * 60000) / 604800000),
        floor_date: d => {
            return [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - (d.getUTCDay() + 6) % 7];
        },
        add_date: (d, n) => {
            return [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n * 7];
        },
        sub_date: (a, b) => (+a - +b) / 604800000
    }

    export const month: YTimeScale & YDateScale = {
        duration: 2592000000,
        floor: a => {
            a.setDate(1);
            a.setHours(0, 0, 0, 0);
        },
        add: (a, d) => { a.setMonth(a.getMonth() + d) },
        sub: (a, b) => (a.getMonth() - b.getMonth() + (a.getFullYear() - b.getFullYear()) * 12),
        floor_date: d => {
            return [d.getUTCFullYear(), d.getUTCMonth(), 1];
        },
        add_date: (d, n) => {
            return [d.getUTCFullYear(), d.getUTCMonth() + n, d.getUTCDate()];
        },
        sub_date: (a, b) => (a.getUTCMonth() - b.getUTCMonth() + (a.getUTCFullYear() - b.getUTCFullYear()) * 12)
    }

    export const year: YTimeScale & YDateScale = {
        duration: 31536000000,
        floor: (a) => {
            a.setMonth(0, 1);
            a.setHours(0, 0, 0, 0);
        },
        add: (a, d) => { a.setFullYear(a.getFullYear() + d) },
        sub: (a, b) => (a.getFullYear() - b.getFullYear()),
        floor_date: d => {
            return [d.getUTCFullYear(), 0, 1];
        },
        add_date: (d, n) => {
            return [d.getUTCFullYear() + n, d.getUTCMonth(), d.getUTCDate()];
        },
        sub_date: (a, b) => (a.getUTCFullYear() - b.getUTCFullYear())
    }

    export function compare(a: YTime | YDate, b: YTime | YDate) {
        return a.n - b.n;
    }

    export function range(a: YTime, b: YTime, scale: YTimeScale, step: number = 1): YTime[] {
        if(b.n < a.n) return range(b, a, scale, -step).reverse();
        if(step <= 0) return [];
        const r: YTime[] = [];
        a = a.ceil(scale, step);
        while(a.before(b)) {
            r.push(a)
            // TODO: maybe optimize somehow
            a = a.add(step, scale).floor(scale);
        }
        return r;
    }

    export function make(year: number, month: number, day?: number, hour?: number, minute?: number, second?: number, millisecond?: number) {
        return new YTime(new Date(year, month, day || 1, hour || 0, minute || 0, second || 0, millisecond || 0).getTime());
    }

    export function now() {
        return new YTime(Date.now());
    }

    export function normalize(time: YTime | YTimeString | number | Date | undefined) {
        return (time instanceof YTime) ? time : $time(time);
    }
}

export function $date(date: YTimeString | YDateString | number | undefined | Date | YTime | YDate) {
    if(!date) return new YDate;
    if(date instanceof YDate) return date;
    if(date instanceof YTime) return date.date;

    if(date === '0001-01-01T00:00:00Z')
        return new YDate;

    let d: Date;

    if(typeof date === 'string' || typeof date === 'number')
        d = new Date(date);
    else
        d = date;

    return new YDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export namespace $date {
    export function make(year: number, month: number, day?: number) {
        return new YDate(year, month, day || 1);
    }

    export function maybe(date: YTimeString | number | Date | undefined) {
        if(!date) return;

        return $date(date);
    }

    export function today() {
        const d = new Date;
        return new YDate(d.getFullYear(), d.getMonth(), d.getDate());
    }
}

export function setTimeoutAt(handler: TimerHandler, when: YTime) {
    const wait = when.sub($time.now(), $time.millisecond);
    return setTimeout(handler, wait > 0 ? wait : 0);
}

export function yIntervalTime(s: YTime, e: YTime | undefined, tz: string) {
    if(!e || !e.n)
        return s.formatIn('EEE d MMM yyyy HH:mm', tz);

    if(s.year !== e.year || s.month !== e.month || s.day_of_month !== e.day_of_month) {
        return s.format('d MMM yyyy HH:mm') + ' - ' + e.format('d MMM yyyy HH:mm');
    } else {
        return s.format('EEE d MMM yyyy, HH:mm') + ' - ' + e.format('HH:mm');
    }
}
